import { 
  Card, 
  Stack, 
  Box, 
  Typography, 
  Chip, 
  Grid, 
  Paper,
  IconButton,
  Tooltip,
  Divider
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import refreshFill from "@iconify/icons-eva/refresh-fill";
import { FEATURE } from "src/util/enums";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList } from "src/hooks/useFetch.hook";
import Page from "../Page";
import { BaseTable } from "../BaseTable/BaseTable";
import { chargerPointColumns } from "./charger.constants";
import { useDispatch, useSelector } from "react-redux";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { RemoteOperationModal } from "./RemoteOperation/RemoteOperationModal";
import { ViewLogsModal } from "./ViewLogsModal";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import RemoteOpsStatusModal from "./RemoteOperation/RemoteOpsStatusModal";
import { remoteOpsStatusSlice } from "src/store/remoteOpsStatus/remoteOpsStatus.slice";
import { remoteOperations } from "./RemoteOperation/remote.constants";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { yearFirstDateTimeFormat } from "src/utils/formatTime";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import AddNewBtn from "../AddNewBtn";
import ExportCSV from "../ExportCsv/ExportCSV";
import { useQueryClient } from "@tanstack/react-query";

// ----------------------------------------------------------------------

const StyledListCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.grey[200]}`,
  background: theme.palette.background.paper,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s ease',
  '&:hover': {
    boxShadow: theme.customShadows.z8,
  }
}));

export default function ChargerList() {
  const hasCreateAccess = useAccess(FEATURE.CHARGER, "C");
  const isReadOnly = useAccess(FEATURE.CHARGER, "R");
  const { data: chargers, isFetching } = useList("chargers");
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { open, data } = useSelector(({ dialog }) => dialog);
  const {modalOpen,remoteOperationPerformed,operationDetails} = useSelector(({remoteOpStatus})=>remoteOpStatus)
  const [sortModel,setSortModel] = useState([{field:"status",sort:"asc"}]);
  const [tableData, setTableData] = useState();
  const [searchValue, setSearchValue] = useState("");
  const [filteredCount, setFilteredCount] = useState(0);
  const prevChargersRef = useRef();
  const handleClose = () => {
    dispatch(dialogSlice.actions.setDialog(false));
  };

  useEffect(() => {
    if (!isFetching && chargers && Array.isArray(chargers)){
      // Check if chargers data has actually changed to prevent infinite loops
      const chargersString = JSON.stringify(chargers);
      if (prevChargersRef.current !== chargersString) {
        let data = [...chargers];
        
        // Reapply search filter if there's an active search
        if (searchValue) {
          const filteredDataArr = data.reduce(
            (filteredDataArr, currentObj) => {
              const relevantFields = [
                "chargepoint_id",
                "team_name",
                "street",
                "description",
                "visibility",
                "status",
                "lastHeartbeatTimestamp",
              ];
              const allValuesArr = Object.entries(currentObj)
                .filter(([key, value]) => relevantFields.includes(key))
                .map(([key, value]) =>
                  key === "lastHeartbeatTimestamp"
                    ? yearFirstDateTimeFormat(value).toString()
                    : value === "Y" ? "active" : value === "N" ? "inactive" : value === 1 ? "public" : value === 0 ? "private" : value ? value.toString().toLowerCase() : ""
                );
              const searchResult = allValuesArr.find((value) =>
                value.includes(searchValue)
              );
              if (searchResult) {
                filteredDataArr = [...filteredDataArr, currentObj];
              }
              return filteredDataArr;
            },
            []
          );
          data = filteredDataArr;
        }
        
        setTableData(data);
        setFilteredCount(data.length);
        prevChargersRef.current = chargersString;
      }
    }
  }, [isFetching, chargers, searchValue]);

  // Calculate statistics from current table data (filtered data)
  const currentData = tableData || chargers || [];
  const totalChargers = currentData.length;
  const activeChargers = currentData.filter(charger => charger.status === "Active")?.length || 0;
  const inactiveChargers = currentData.filter(charger => charger.status === "Inactive")?.length || 0;
  const publicChargers = currentData.filter(charger => Number(charger.visibility) === 1)?.length || 0;
  const privateChargers = currentData.filter(charger => Number(charger.visibility) === 0)?.length || 0;

  const handleRemoteOpsStatusModalClose =()=>{
  dispatch(remoteOpsStatusSlice.actions.setRemoteOps({open:false,remoteOperationPerformed:null,operationDetails:{chargerId:null,response:null}}));
    
  }

  const addNewChargerBtnHandler =()=> dispatch(tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
    detailsId:"",
    tabOpenedFor:"charger",
    tabOpened:"general"
  }))

  const handleSearching = (event) => {
    const dataToSearchFrom = chargers || [];
    const searchValue = event.target.value.trim().toString().toLowerCase();
    setSearchValue(searchValue);
    
    if (searchValue) {
      const filteredDataArr = dataToSearchFrom.reduce(
        (filteredDataArr, currentObj) => {
          const relevantFields = [
            "chargepoint_id",
            "team_name",
            "street",
            "description",
            "visibility",
            "status",
            "lastHeartbeatTimestamp",
          ];
          const allValuesArr = Object.entries(currentObj)
            .filter(([key, value]) => relevantFields.includes(key))
            .map(([key, value]) =>
              key === "lastHeartbeatTimestamp"
                ? yearFirstDateTimeFormat(value).toString()
                :  value === "Y" ? "active" : value === "N" ? "inactive" : value === 1 ? "public" : value === 0 ? "private" : value ?  value.toString().toLowerCase() : ""
                );
          const searchResult = allValuesArr.find((value) =>
            value.includes(searchValue)
          );
          if (searchResult) {
            filteredDataArr = [...filteredDataArr, currentObj];
          }
          return filteredDataArr;
        },
        []
      );
      setTableData(filteredDataArr);
      setFilteredCount(filteredDataArr.length);
    } else {
      setTableData(chargers && Array.isArray(chargers) ? [...chargers] : []);
      setFilteredCount(chargers?.length || 0);
    }
  };
  
  const handleRefresh = () => {
    // Invalidate all charger queries to force a fresh fetch
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === "getChargers"
    });
  }
  return (
    <Page
      {...(hasCreateAccess && { path: "new",action: addNewChargerBtnHandler  })}
      actionButtonText="Add Charge Point"
    >
      {/* Header Section */}
      <Box sx={{ mb: 3 }}>
        {/* <Typography variant="h4" sx={{ 
          fontWeight: 600, 
          color: (theme) => theme.palette.primary.main,
          mb: 1
        }}>
          Charge Points Management
        </Typography> */}
        <Typography variant="body1" sx={{ 
          color: (theme) => theme.palette.text.secondary,
          mb: 3
        }}>
          Manage and monitor your charging infrastructure
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.primary.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.primary.lighter,
                  mr: 1.5
                }}>
                  <Icon icon="eva:charging-outline" width={20} height={20} style={{ 
                    color: (theme) => theme.palette.primary.main 
                  }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Chargers
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.primary.main 
              }}>
                {totalChargers}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.success.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.success.lighter,
                  mr: 1.5
                }}>
                  <Icon icon="eva:checkmark-circle-outline" width={20} height={20} style={{ 
                    color: (theme) => theme.palette.success.main 
                  }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Active Chargers
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.success.main 
              }}>
                {activeChargers}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.error.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.error.lighter,
                  mr: 1.5
                }}>
                  <Icon icon="eva:close-circle-outline" width={20} height={20} style={{ 
                    color: (theme) => theme.palette.error.main 
                  }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Inactive Chargers
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.error.main 
              }}>
                {inactiveChargers}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ 
              p: 2, 
              borderRadius: 2,
              background: (theme) => theme.palette.background.paper,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: (theme) => theme.palette.info.main,
              }
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 1.5,
                  background: (theme) => theme.palette.info.lighter,
                  mr: 1.5
                }}>
                  <Icon icon="eva:globe-outline" width={20} height={20} style={{ 
                    color: (theme) => theme.palette.info.main 
                  }} />
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Public Chargers
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ 
                fontWeight: 700, 
                color: (theme) => theme.palette.info.main 
              }}>
                {publicChargers}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Header Section */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Charge Points
          </Typography>
          {searchValue && (
            <Chip
              label={`${filteredCount} of ${chargers?.length || 0} chargers`}
              color="primary"
              size="small"
              sx={{ fontWeight: 500 }}
            />
          )}
        </Box>
      </Stack>
      
      {/* Combined Search and Table Card */}
      <StyledListCard>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CommonSearchbar
              placeholder="Search by charger ID, team, address, description, or status..."
              searchFunc={(e) => handleSearching(e)}
            />
          </Box>
          <Tooltip title="Refresh data">
            <span>
              <IconButton
                onClick={handleRefresh}
                disabled={isFetching}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                  },
                  '&:disabled': {
                    bgcolor: 'grey.300',
                    color: 'grey.500',
                  },
                }}
              >
                <Icon icon={refreshFill} width={20} height={20} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <BaseTable
          rows={tableData ?? []}
          columns={chargerPointColumns(isReadOnly)}
          loading={isFetching}
          getRowId={(row) => row.chargepoint_pk}
          pageSize={10}
          sortModel={sortModel}
          onSortModelChange={(model)=>setSortModel(model)}
          isReadOnly={isReadOnly}
          showExportCsvBtn={true}
          csvMorphemes="chargers"
          csvColumns={chargerPointColumns(isReadOnly)}
          height={Math.max(312, (tableData?.length || 0) * 52 + 100)}
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
            },
            '& .MuiDataGrid-main': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-virtualScroller': {
              height: 'auto !important',
              minHeight: 'auto !important',
            },
            '& .MuiDataGrid-row': {
              minHeight: '52px !important',
              height: 'auto !important',
              '& .MuiDataGrid-cell': {
                minHeight: '52px !important',
                height: 'auto !important',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'flex-start',
                '&:focus': {
                  outline: 'none',
                }
              }
            }
          }}
        />
      </StyledListCard>
      
      {open && data?.modalType === "remoteoperation" && (
        <RemoteOperationModal
          open={open}
          chargerId={data?.id}
          chargerPk={data?.pk}
          onClose={handleClose}
        />
      )}
    </Page>
  );
}
